/**
 * Decide whether a CSR terminal socket event should clear local call UI
 * for the currently open customer chat.
 *
 * Safety: never clear another customer's in-flight call.
 */

/** Parse customer user id from `csr_{adminId}_{customerId}_{ts}`. */
export function parseCsrChannelCustomerId(channelName) {
  const parts = String(channelName || "")
    .trim()
    .split("_");
  if (parts.length < 4 || parts[0].toLowerCase() !== "csr") return null;
  const customerId = Number(parts[2]);
  return Number.isFinite(customerId) && customerId > 0 ? customerId : null;
}

/**
 * @param {{
 *   eventChannelName?: string|null,
 *   activeChannelName?: string|null,
 *   openCustomerUserId?: number|string|null,
 * }} args
 * @returns {boolean}
 */
export function shouldClearCsrLocalCallOnTerminal({
  eventChannelName,
  activeChannelName,
  openCustomerUserId,
} = {}) {
  const eventCh = String(eventChannelName || "").trim();
  if (!eventCh) return false;

  const activeCh = String(activeChannelName || "").trim();
  // Exact channel match — clear this chat's call.
  if (activeCh && eventCh === activeCh) return true;

  // No local in-flight call — event is harmless.
  if (!activeCh) return false;

  // Stale/mismatched identity: only clear when the event is a csr_* channel
  // for the same open customer (this chat instance has one active call).
  const eventCustomerId = parseCsrChannelCustomerId(eventCh);
  const openId = Number(openCustomerUserId);
  if (
    eventCustomerId != null &&
    Number.isFinite(openId) &&
    openId > 0 &&
    eventCustomerId === openId
  ) {
    return true;
  }

  // Other customer's terminal event — do not touch this chat's call.
  return false;
}
